// Login session storage.
//
// The session lives in sessionStorage, so it ends when the tab closes. A newly opened tab asks the
// other open tabs of this site for their session over a BroadcastChannel; if none answers, every tab
// was closed and the user has to log in again. Logging out in one tab logs out all of them.

const TOKEN_KEY = 'token';
const USER_KEY = 'user';
const HANDOFF_WAIT_MS = 300;

const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('inv-session') : null;
const remoteLogoutListeners = new Set();

export const getToken = () => sessionStorage.getItem(TOKEN_KEY);

export const getUser = () => {
  const stored = sessionStorage.getItem(USER_KEY);
  return stored ? JSON.parse(stored) : null;
};

export function setSession(token, user) {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession({ broadcast = false } = {}) {
  const username = getUser()?.username;
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  if (broadcast && username) channel?.postMessage({ type: 'logout', username });
}

// Called when another tab logs out. Returns an unsubscribe function.
export function onRemoteLogout(listener) {
  remoteLogoutListeners.add(listener);
  return () => remoteLogoutListeners.delete(listener);
}

channel?.addEventListener('message', ({ data }) => {
  if (data?.type === 'request') {
    const token = getToken();
    if (token) channel.postMessage({ type: 'session', token, user: getUser() });
  } else if (data?.type === 'logout' && data.username === getUser()?.username) {
    // Every vendor shares one admin origin, so only tabs of the same account follow a logout;
    // a tab logged into another shop in this browser stays logged in.
    clearSession();
    remoteLogoutListeners.forEach((listener) => listener());
  }
});

// Run once before the app renders.
export async function initSession() {
  // Sessions used to be kept in localStorage (surviving browser restarts); drop any left over.
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);

  if (getToken() || !channel) return; // refreshed / duplicated tab, or no cross-tab support

  await new Promise((resolve) => {
    const timer = setTimeout(done, HANDOFF_WAIT_MS);
    function onMessage({ data }) {
      if (data?.type === 'session' && data.token) {
        setSession(data.token, data.user);
        done();
      }
    }
    function done() {
      clearTimeout(timer);
      channel.removeEventListener('message', onMessage);
      resolve();
    }
    channel.addEventListener('message', onMessage);
    channel.postMessage({ type: 'request' });
  });
}
